/**
 * Odvozený render model pro TreeView dle „Anatomie tree-view" (PHX-TREE-01 v1.1).
 *
 * Pipeline: typová viditelnost → normalizovaný textový match → varianta B
 * („rozbalit lze vždy + filtr uzlu"): kořeny omezené na shody, auto-expand
 * uzlů se shodou pod sebou, profiltrovaný výpis se štítkem, ruční tri-state
 * zásahy s předností, odkrytí výpisu per-uzel → flatten.
 */
import type {
  TreeCheckState,
  TreeData,
  TreeNode,
  TreeRowChip,
  TreeViewRow,
  TreeViewState,
} from './treeTypes';

/** Znaky, které NFD dekompozice nepokryje (spec vyžaduje ł → l). */
const SPECIAL_NORMALIZATION: Record<string, string> = { ł: 'l', Ł: 'l', đ: 'd', Đ: 'd' };

const COMBINING_MARKS = /[̀-ͯ]/g;

/**
 * Normalizace pro vyhledávání: lowercase + odstranění diakritiky (vč. ł → l).
 * Zachovává 1:1 mapování UTF-16 indexů na vstup, takže nalezené rozsahy
 * v normalizovaném textu lze přímo použít pro zvýraznění v originálu.
 */
export function normalizeSearchText(text: string): string {
  let result = '';
  for (const ch of text) {
    if (ch.length > 1) {
      // Astrální pár — beze změny, aby se zachovala délka v UTF-16 jednotkách.
      result += ch;
      continue;
    }
    const special = SPECIAL_NORMALIZATION[ch];
    if (special) {
      result += special;
      continue;
    }
    const lower = ch.toLowerCase();
    const base = lower.normalize('NFD').replace(COMBINING_MARKS, '');
    result += base.length === 1 ? base : (base[0] ?? lower[0] ?? ch);
  }
  return result;
}

/** Kompletní zobrazený text uzlu, ve kterém se hledá podřetězec. */
export function getNodeSearchText(node: TreeNode): string {
  const parts: string[] = [];
  if (node.code != null) parts.push(node.code);
  if (node.labelSegments?.length) {
    for (const segment of node.labelSegments) parts.push(segment.text);
  } else {
    parts.push(node.label);
  }
  return parts.join(' ');
}

/**
 * Rozsahy `[od, do)` výskytů normalizovaného dotazu v textu — indexy platí
 * v originálním textu (normalizace zachovává délku).
 */
export function findMatchRanges(text: string, normalizedQuery: string): Array<[number, number]> {
  if (!normalizedQuery) return [];
  const normalizedText = normalizeSearchText(text);
  const ranges: Array<[number, number]> = [];
  let from = 0;
  let index: number;
  while ((index = normalizedText.indexOf(normalizedQuery, from)) !== -1) {
    ranges.push([index, index + normalizedQuery.length]);
    from = index + normalizedQuery.length;
  }
  return ranges;
}

/** Match info uzlu: vlastní shoda + počet shod v podstromu. */
export interface TreeMatchInfo {
  self: boolean;
  descMatches: number;
}

/**
 * Jeden DFS průchod: pro každý typově viditelný uzel spočítá vlastní shodu
 * a počet shod pod ním. Uzly skrytého typu (včetně podstromů) v mapě nejsou —
 * mapa tak zároveň slouží jako množina přítomných uzlů.
 */
export function computeMatchInfo(
  data: TreeData,
  visibleTypes: ReadonlySet<string>,
  normalizedQuery: string
): Map<string, TreeMatchInfo> {
  const result = new Map<string, TreeMatchInfo>();

  function visit(nodeId: string): TreeMatchInfo | null {
    const node = data.nodesById[nodeId];
    if (!node || !visibleTypes.has(node.type)) return null;
    const self =
      normalizedQuery.length > 0 &&
      normalizeSearchText(getNodeSearchText(node)).includes(normalizedQuery);
    let descMatches = 0;
    for (const childId of node.childIds) {
      const childInfo = visit(childId);
      if (childInfo) descMatches += (childInfo.self ? 1 : 0) + childInfo.descMatches;
    }
    const info: TreeMatchInfo = { self, descMatches };
    result.set(nodeId, info);
    return info;
  }

  for (const rootId of data.rootIds) visit(rootId);
  return result;
}

/**
 * Tvrdý reset rozbalení na úroveň `level` (klik na tečku během hledání):
 * explicitní override pro každý uzel s potomky — rozbaleno právě tak,
 * aby byly vidět uzly úrovně `level`.
 */
export function getLevelBaselineOverrides(data: TreeData, level: number): Map<string, boolean> {
  const overrides = new Map<string, boolean>();

  function walk(nodeId: string, depth: number) {
    const node = data.nodesById[nodeId];
    if (!node || node.childIds.length === 0) return;
    overrides.set(node.id, depth + 1 < level);
    for (const childId of node.childIds) walk(childId, depth + 1);
  }

  for (const rootId of data.rootIds) walk(rootId, 0);
  return overrides;
}

/** Rozbalení celé cesty od kořene k uzlu (zrušení hledání s výběrem). */
export function getPathOverrides(data: TreeData, nodeId: string): Map<string, boolean> {
  const overrides = new Map<string, boolean>();
  let current = data.nodesById[nodeId];
  while (current?.parentId) {
    overrides.set(current.parentId, true);
    current = data.nodesById[current.parentId];
  }
  return overrides;
}

/**
 * Hlavní derivační pipeline — vrací ploché pole řádků pro render.
 *
 * Priority dle PHX-TREE-01 § 8:
 * - rozbalení: ruční zásah > auto-expand při hledání > úroveň dle tečky,
 * - výpis potomků: odkrytí štítkem > filtr hledání (uzel se shodami) > úplný výpis.
 */
export function deriveVisibleRows(
  data: TreeData,
  state: TreeViewState
): { rows: TreeViewRow[]; normalizedQuery: string } {
  const { nodesById, rootIds } = data;
  const { query, visibleTypes, manualOverrides, revealedIds, baseLevel, activeNodeId } = state;

  const normalizedQuery = normalizeSearchText(query.trim());
  const searching = normalizedQuery.length > 0;
  const matchInfo = computeMatchInfo(data, visibleTypes, normalizedQuery);

  const isRelevant = (nodeId: string): boolean => {
    const info = matchInfo.get(nodeId);
    return info != null && (info.self || info.descMatches > 0);
  };

  const rows: TreeViewRow[] = [];

  function visit(nodeId: string, depth: number) {
    const node = nodesById[nodeId];
    const info = matchInfo.get(nodeId);
    if (!node || !info) return;

    const children = node.childIds.filter((childId) => matchInfo.has(childId));
    const hasChildren = children.length > 0;

    const manual = manualOverrides.get(nodeId);
    let isExpanded = false;
    if (hasChildren) {
      if (manual !== undefined) isExpanded = manual;
      else if (searching) isExpanded = info.descMatches > 0;
      else isExpanded = depth + 1 < baseLevel;
    }

    let listedChildren = children;
    let chip: TreeRowChip | undefined;
    if (searching && hasChildren) {
      const relevantChildren = children.filter(isRelevant);
      const filtered = relevantChildren.length < children.length;
      if (revealedIds.has(nodeId)) {
        if (isExpanded && filtered) {
          chip = { shown: relevantChildren.length, total: children.length, revealed: true };
        }
      } else if (info.descMatches > 0) {
        listedChildren = relevantChildren;
        if (isExpanded && filtered) {
          chip = { shown: relevantChildren.length, total: children.length, revealed: false };
        }
      }
      // Uzel bez shod pod sebou: úplný výpis bez štítku (ruční průzkum).
    }

    rows.push({
      node,
      depth,
      isExpanded,
      hasChildren,
      isActive: nodeId === activeNodeId,
      isMatched: searching && info.self,
      ...(chip ? { chip } : {}),
    });

    if (isExpanded) {
      for (const childId of listedChildren) visit(childId, depth + 1);
    }
  }

  const presentRoots = rootIds.filter((rootId) => matchInfo.has(rootId));
  const visibleRoots = searching ? presentRoots.filter(isRelevant) : presentRoots;
  for (const rootId of visibleRoots) visit(rootId, 0);

  return { rows, normalizedQuery };
}

/** Posbírej id všech listových potomků uzlu (uzel bez childIds je listem sám) */
export function collectLeafIds(data: TreeData, nodeId: string): string[] {
  const node = data.nodesById[nodeId];
  if (!node) return [];
  if (node.childIds.length === 0) return [node.id];
  const leaves: string[] = [];
  for (const childId of node.childIds) {
    leaves.push(...collectLeafIds(data, childId));
  }
  return leaves;
}

/**
 * Odvodí tri-state checkboxů pro všechny uzly:
 * list = členství v `checkedIds`; vnitřní uzel = all/none/mixed přes listové potomky.
 * Jeden DFS průchod s memoizací, O(n).
 */
export function deriveCheckStates(
  data: TreeData,
  checkedIds: ReadonlySet<string>
): Map<string, TreeCheckState> {
  const states = new Map<string, TreeCheckState>();

  function visit(nodeId: string): TreeCheckState {
    const cached = states.get(nodeId);
    if (cached) return cached;

    const node = data.nodesById[nodeId];
    if (!node) return 'unchecked';

    let state: TreeCheckState;
    if (node.childIds.length === 0) {
      state = checkedIds.has(node.id) ? 'checked' : 'unchecked';
    } else {
      let hasChecked = false;
      let hasUnchecked = false;
      for (const childId of node.childIds) {
        const childState = visit(childId);
        if (childState === 'indeterminate') {
          hasChecked = true;
          hasUnchecked = true;
        } else if (childState === 'checked') {
          hasChecked = true;
        } else {
          hasUnchecked = true;
        }
        if (hasChecked && hasUnchecked) break;
      }
      state = hasChecked && hasUnchecked ? 'indeterminate' : hasChecked ? 'checked' : 'unchecked';
    }

    states.set(nodeId, state);
    return state;
  }

  for (const node of Object.values(data.nodesById)) {
    visit(node.id);
  }

  return states;
}

/**
 * Toggle checkboxu uzlu s kaskádou na listové potomky:
 * checked/indeterminate → odebrat všechny listové potomky; unchecked → přidat je.
 * Vrací novou množinu (vstup nemutuje).
 */
export function toggleNodeChecked(
  data: TreeData,
  checkedIds: ReadonlySet<string>,
  nodeId: string
): Set<string> {
  const next = new Set(checkedIds);
  const leaves = collectLeafIds(data, nodeId);
  const allChecked = leaves.length > 0 && leaves.every((id) => next.has(id));
  for (const id of leaves) {
    if (allChecked) {
      next.delete(id);
    } else {
      next.add(id);
    }
  }
  return next;
}
